import { Furniture } from './../entities/Furniture';
import { Shelf } from './../entities/Shelf';
import {
  GALVA_COLOR,
  INOX_COLOR,
  WHITE_COLOR,
  BLACK_COLOR,
  CabinetSupport,
} from './../constants';
import { Cabinet } from '../entities/Cabinet';
import descriptionGeneratorData from './descriptionGeneratorData.json';
import { PositionAngle } from '../constants';
import { getCabinetNameWithoutDimensions } from './nameGenerator';

function getTranslatedText(
  text: string,
  language: string = 'en',
  variables: { [id: string]: string } = {},
) {
  const translations = descriptionGeneratorData as any;
  let textTranslated = text;
  if (language in translations && text in translations[language])
    textTranslated = translations[language][text];

  for (const variable in variables)
    textTranslated = textTranslated.replace(variable, variables[variable]);

  return textTranslated;
}

function getCornersPart(cabinet: Cabinet, language: string = 'en'): string {
  const frontLeftCorner = cabinet.getCorner(PositionAngle.FrontLeft);
  if (
    frontLeftCorner &&
    frontLeftCorner.color &&
    frontLeftCorner.color.reference
  ) {
    switch (frontLeftCorner.color.reference) {
      case GALVA_COLOR:
        return getTranslatedText('with galvanized metals corner profiles', language);
      case INOX_COLOR:
        return getTranslatedText(
          'with stainless steel metals corner profiles',
          language,
        );
      case WHITE_COLOR:
        return getTranslatedText('with white metals corner profiles', language);
      case BLACK_COLOR:
        return getTranslatedText('with black metals corner profiles', language);
      default:
        break;
    }
  }
  return '';
}

function getShelvesHeightsDescendingOrder(shelvesHeightsCounters: {
  [id: number]: number;
}) {
  return Object.keys(shelvesHeightsCounters).sort(function(
    a: string,
    b: string,
  ) {
    return parseInt(b) - parseInt(a);
  });
}

function getShelvesHeightsCounters(cabinet: Cabinet): { [id: number]: number } {
  let shelvesHeightsCounters: { [id: number]: number } = {};
  for (const shelf of cabinet.shelves) {
    if (shelf.height.toString() in shelvesHeightsCounters)
      shelvesHeightsCounters[shelf.height]++;
    else shelvesHeightsCounters[shelf.height] = 1;
  }

  return shelvesHeightsCounters;
}

function getShelfPart(
  shelfHeight: number,
  nbShelves: number,
  language: string = 'en',
) {
  const variables = {
    '%number%': nbShelves.toString(),
    '%height%': (shelfHeight / 10).toString(),
  };
  const text =
    nbShelves > 1
      ? '%number% compartments of %height% cm'
      : '%number% compartment of %height% cm';

  return getTranslatedText(text, language, variables);
}

function getShelvesHeightsText(
  cabinet: Cabinet,
  language: string = 'en',
): string {
  const shelvesHeightsCounters = getShelvesHeightsCounters(cabinet);
  const shelvesHeights = getShelvesHeightsDescendingOrder(
    shelvesHeightsCounters,
  );

  let shelvesHeightsText = '';
  let count = 0;
  for (const shelfHeight of shelvesHeights) {
    if (shelvesHeights.length > 1 && count === shelvesHeights.length - 1)
      shelvesHeightsText += ' ' + getTranslatedText('and', language) + ' ';
    else if (count !== 0) shelvesHeightsText += ', ';

    const nbShelves = shelvesHeightsCounters[parseInt(shelfHeight)];
    shelvesHeightsText += getShelfPart(
      parseInt(shelfHeight),
      nbShelves,
      language,
    );

    count++;
  }

  return shelvesHeightsText;
}

function getShelvesPart(cabinet: Cabinet, language: string = 'en'): string {
  const textBefore = 'Compartments:';

  return (
    getTranslatedText(textBefore, language) +
    ' ' +
    getShelvesHeightsText(cabinet, language)
  );
}

function getSupportPart(
  cabinet: Cabinet,
  language: string = 'en',
): string | undefined {
  switch (cabinet.support) {
    case CabinetSupport.Feet:
      const feetsHeight =
        cabinet.feets && cabinet.feets[0] ? cabinet.feets[0].height / 10 : 0;
      return getTranslatedText('on feet of %height% cm', language, {
        '%height%': feetsHeight.toString(),
      });
    case CabinetSupport.AdjustableFeet:
      return getTranslatedText(
        'on adjustable feet of 11.5 to 13 cm',
        language,
      );
    case CabinetSupport.ToHangHook:
    case CabinetSupport.ToHangScrew:
      return getTranslatedText('to hang', language);
    case CabinetSupport.WheelBlack:
    case CabinetSupport.WheelWhite:
      return getTranslatedText('on castors', language);
  }
  return undefined;
}

export function getCabinetDescription(
  cabinet: Cabinet,
  language: string = 'en',
): string {
  const supportPart = getSupportPart(cabinet, language);
  const firstPart =
    getCabinetNameWithoutDimensions(cabinet, language) +
    ' ' +
    getCornersPart(cabinet, language) +
    (supportPart ? ' ' + supportPart : '') +
    '.';

  const secondPart = getShelvesPart(cabinet, language) + '.';

  if (firstPart.length + secondPart.length > 250) return firstPart;

  return firstPart + ' ' + secondPart;
}

export function getFurnitureDescription(
  furniture: Furniture,
  language: string = 'en',
): string {
  if (furniture.cabinets.length === 1)
    return getCabinetDescription(furniture.cabinets[0].cabinet, language);

  const cabinetNamesArray = furniture.cabinets.map(cabinetEntry => {
    const name = cabinetEntry.cabinet.getName(language);
    return name.charAt(0).toLowerCase() + name.slice(1);
  });

  const firstPart = getTranslatedText(
    'Set of %number% pieces of furniture',
    language,
    {
      '%number%': furniture.cabinets.length.toString(),
    },
  );

  const secondPart = cabinetNamesArray.join(', ');

  if (firstPart.length + secondPart.length > 250) return firstPart;

  return firstPart + getTranslatedText(':', language) + ' ' + secondPart;
}

export function getCabinetDescriptions(
  cabinet: Cabinet,
): { [id: string]: string } {
  let cabinetNames: { [id: string]: string } = {};

  const translations = descriptionGeneratorData as any;
  for (const language in translations)
    cabinetNames[language] = getCabinetDescription(cabinet, language);

  cabinetNames['en'] = getCabinetDescription(cabinet, 'en');

  return cabinetNames;
}

export function getFurnitureDescriptions(
  futniture: Furniture,
): { [id: string]: string } {
  let cabinetNames: { [id: string]: string } = {};

  const translations = descriptionGeneratorData as any;
  for (const language in translations)
    cabinetNames[language] = getFurnitureDescription(futniture, language);

  cabinetNames['en'] = getFurnitureDescription(futniture, 'en');

  return cabinetNames;
}
